// deriveJourneySteps — pure derivation of granular Trip_Steps for the
// chronological timeline's Departure and Return phases, plus event → step
// mappers for the Destination phase.
//
// The granular departure/return steps (leave home, parking, security, gate,
// landing, arrive home) are anchored on the SAME boundary timestamps the
// journey stage engine resolves against — drawn from
// `computeStageTimeline(flight, null, depCalc)` — so the timeline can never
// drift from the engine. Each step's Detail_Pairs come from `buildStepDetails`
// and its Contextual_Tip from `generateTip`, composing the existing kernels.
//
// Every function here is TOTAL: it never throws for any input shape. Steps
// whose required inputs are absent (no home address, no parking record, no
// traffic/arrival data, missing/invalid dates) are OMITTED rather than
// fabricated with placeholder times, and a non-fatal note is pushed onto the
// returned `errors` array. The flight-anchored steps (check-in/security and
// gate/boarding) are always produced when the flight's boundary times resolve,
// guaranteeing graceful degradation.
//
// Validates: Requirements 5.1, 5.2, 5.3, 5.4, 3.7, 13.5

import {
  STAGES,
  computeStageTimeline,
} from '../../services/journeyStageEngine';
import { buildStepDetails } from './buildStepDetails';
import { generateTip } from './generateTip';

// ── Known step types ────────────────────────────────────────
// The complete, closed set of `stepType` values this module is allowed to
// produce (Property 6c). Any derivation that cannot map onto one of these is
// dropped rather than inventing a new type.
export const STEP_TYPES = Object.freeze({
  LEAVE_HOME: 'leave_home',
  PARKING: 'parking',
  SECURITY: 'security',
  GATE: 'gate',
  FLIGHT_ARRIVAL: 'flight_arrival',
  LANDING: 'landing',
  GROUND_TRANSPORT: 'ground_transport',
  HOTEL_CHECKIN: 'hotel_checkin',
  HOTEL_CHECKOUT: 'hotel_checkout',
  DINING: 'dining',
  MEETING: 'meeting',
  ARRIVE_HOME: 'arrive_home',
});

const KNOWN_STEP_TYPES = new Set(Object.values(STEP_TYPES));

// lucide-react icon keys per step type (resolved to components by the
// presentation layer). Kept as strings so the pure model carries no React.
const STEP_ICONS = {
  [STEP_TYPES.LEAVE_HOME]: 'MapPin',
  [STEP_TYPES.PARKING]: 'CircleParking',
  [STEP_TYPES.SECURITY]: 'ShieldCheck',
  [STEP_TYPES.GATE]: 'Plane',
  [STEP_TYPES.FLIGHT_ARRIVAL]: 'PlaneLanding',
  [STEP_TYPES.LANDING]: 'PlaneLanding',
  [STEP_TYPES.GROUND_TRANSPORT]: 'Car',
  [STEP_TYPES.HOTEL_CHECKIN]: 'BedDouble',
  [STEP_TYPES.HOTEL_CHECKOUT]: 'BedDouble',
  [STEP_TYPES.DINING]: 'Utensils',
  [STEP_TYPES.MEETING]: 'Calendar',
  [STEP_TYPES.ARRIVE_HOME]: 'Home',
};

// ── Date / formatting helpers ───────────────────────────────

/**
 * Coerce a value to a valid Date, or null when missing/invalid.
 * @param {*} value
 * @returns {Date|null}
 */
function toDate(value) {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * True when `d` is a usable Date (a real instant). Used to gate step creation
 * so we never produce a step anchored on `Invalid Date`.
 * @param {*} d
 * @returns {boolean}
 */
function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

/**
 * Localized "6:30 AM"-style label, or '' when the date is unusable.
 * @param {Date|null} date
 * @returns {string}
 */
function formatTime(date) {
  if (!isValidDate(date)) return '';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * Format a duration in minutes as "45 min" or "1 hr 20 min". Returns null for
 * non-finite / non-positive inputs so the caller can omit the detail.
 * @param {*} minutes
 * @returns {string|null}
 */
function formatMinutes(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return null;
  const rounded = Math.round(n);
  if (rounded < 60) return `${rounded} min`;
  const hrs = Math.floor(rounded / 60);
  const mins = rounded % 60;
  return mins === 0 ? `${hrs} hr` : `${hrs} hr ${mins} min`;
}

/**
 * Format a distance in miles as "22 miles", or null when absent.
 * @param {*} miles
 * @returns {string|null}
 */
function formatMiles(miles) {
  const n = Number(miles);
  if (!Number.isFinite(n) || n <= 0) return null;
  const rounded = Math.round(n * 10) / 10;
  return `${rounded} ${rounded === 1 ? 'mile' : 'miles'}`;
}

/**
 * Returns a trimmed non-empty string, or null.
 * @param {*} value
 * @returns {string|null}
 */
function nonEmpty(value) {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * A home address is "present" when it carries at least a city (mirrors the
 * departureCalculator guard, which treats a city-less address as unusable).
 * @param {Object} [userProfile]
 * @returns {boolean}
 */
function hasHomeAddress(userProfile) {
  const addr = userProfile?.homeAddress;
  return Boolean(addr && nonEmpty(addr.city));
}

/**
 * Pull the drive distance from whichever shape the departure calculation used.
 * @param {Object} [depCalc]
 * @returns {*}
 */
function resolveDistanceMiles(depCalc) {
  return (
    depCalc?.trafficData?.distanceMiles ??
    depCalc?.breakdown?.distanceMiles ??
    depCalc?.distanceMiles ??
    null
  );
}

/**
 * Pull the home → airport drive minutes from the departure calculation.
 * @param {Object} [depCalc]
 * @returns {*}
 */
function resolveTravelMinutes(depCalc) {
  return depCalc?.travelTime ?? depCalc?.breakdown?.travelTime ?? null;
}

// ── Boundary extraction ─────────────────────────────────────

/**
 * Resolve the granular boundary timestamps for a single flight leg from the
 * stage engine's own timeline, so derived step times cannot drift from the
 * engine. Returns a map of `{ stage: { start, end } }` containing only entries
 * whose start and end are valid Dates. Never throws.
 *
 * @param {Object} flight
 * @param {Object} [depCalc]
 * @returns {Object<string, { start: Date, end: Date }>}
 */
function resolveBoundaries(flight, depCalc) {
  const map = {};
  if (!flight || typeof flight !== 'object') return map;
  let timeline;
  try {
    timeline = computeStageTimeline(flight, null, depCalc || null);
  } catch (err) {
    return map; // total: a failing engine call contributes no boundaries
  }
  if (!Array.isArray(timeline)) return map;
  for (const entry of timeline) {
    if (entry && isValidDate(entry.start) && isValidDate(entry.end)) {
      map[entry.stage] = { start: entry.start, end: entry.end };
    }
  }
  return map;
}

// ── Step assembly ───────────────────────────────────────────

/**
 * Assemble a TripStepData, composing `buildStepDetails` and `generateTip`.
 * Returns null when the anchor `scheduledStart` is not a valid Date, so callers
 * can spread the result and rely on falsy filtering.
 *
 * @param {Object} params
 * @param {string} params.id
 * @param {'departure'|'destination'|'return'} params.phase
 * @param {string} params.stepType
 * @param {Date|null} params.scheduledStart
 * @param {Date|null} [params.scheduledEnd]
 * @param {string} params.title
 * @param {string} params.description
 * @param {Object} [params.detailSource]  Display-ready record for buildStepDetails
 * @param {Object} [params.tipSource]     Source record for generateTip
 * @param {Object} [params.ctx]           Traveler context for generateTip
 * @returns {Object|null} TripStepData (without `status`) or null
 */
function makeStep({
  id,
  phase,
  stepType,
  scheduledStart,
  scheduledEnd = null,
  title,
  description,
  detailSource = {},
  tipSource = {},
  ctx = {},
}) {
  if (!isValidDate(scheduledStart)) return null;
  if (!KNOWN_STEP_TYPES.has(stepType)) return null;

  return {
    id,
    phase,
    scheduledStart,
    scheduledEnd: isValidDate(scheduledEnd) ? scheduledEnd : null,
    time: formatTime(scheduledStart),
    stepType,
    icon: STEP_ICONS[stepType] || 'Circle',
    title,
    description,
    details: buildStepDetails(stepType, detailSource, ctx),
    tip: generateTip(stepType, tipSource, ctx),
  };
}

/**
 * Build the display-ready flight detail source (gate / flight / seat / boarding
 * group / departure time) honoring the canonical field names buildStepDetails
 * reads. Absent fields are left undefined so they're omitted downstream.
 *
 * @param {Object} flight
 * @param {Date|null} departureLabelTime
 * @returns {Object}
 */
function flightDetailSource(flight, departureLabelTime) {
  const number = nonEmpty(flight?.flightNumber) || nonEmpty(flight?.airline);
  const depTime = formatTime(departureLabelTime);
  return {
    gate: nonEmpty(flight?.departureGate) || undefined,
    flight: number || undefined,
    seat: nonEmpty(flight?.seatAssignment) || undefined,
    boardingGroup: nonEmpty(flight?.boardingGroup) || undefined,
    departureTime: depTime || undefined,
  };
}

/**
 * Build the arrival/landing flight detail source.
 *
 * @param {Object} flight
 * @returns {Object}
 */
function arrivalDetailSource(flight) {
  const number = nonEmpty(flight?.flightNumber) || nonEmpty(flight?.airline);
  const dep = nonEmpty(flight?.departureAirport);
  const arr = nonEmpty(flight?.arrivalAirport);
  const route = dep && arr ? `${dep} → ${arr}` : undefined;
  return {
    flight: number ? (route ? `${number} · ${route}` : number) : route,
    gate: nonEmpty(flight?.arrivalGate) || undefined,
  };
}

// ── Departure phase ─────────────────────────────────────────

/**
 * Append the "Leave home" step when the engine boundary and a usable home
 * address are present; otherwise record a non-fatal omission note.
 */
function appendLeaveHomeStep(boundary, ctx, deps, steps, errors) {
  if (!boundary) return;
  if (!hasHomeAddress(deps.userProfile)) {
    errors.push({
      message: 'Leave-home step omitted: no home address on the traveler profile.',
    });
    return;
  }
  const driveTime = formatMinutes(resolveTravelMinutes(deps.depCalc));
  const distance = formatMiles(resolveDistanceMiles(deps.depCalc));
  const step = makeStep({
    id: `dep:leave-home:${deps.legId}`,
    phase: 'departure',
    stepType: STEP_TYPES.LEAVE_HOME,
    scheduledStart: boundary.start,
    scheduledEnd: boundary.end,
    title: 'Leave home',
    description:
      'Depart from home to the airport. Allow time for parking, check-in, and security before your flight.',
    detailSource: {
      driveTime: driveTime || undefined,
      distance: distance || undefined,
    },
    ctx,
  });
  if (step) steps.push(step);
}

/**
 * Append the "Airport parking" step when the engine boundary and a parking
 * record are present; otherwise record a non-fatal omission note.
 */
function appendParkingStep(boundary, ctx, deps, steps, errors) {
  if (!boundary) return;
  const parking = deps.parking;
  if (!(parking && typeof parking === 'object')) {
    errors.push({
      message: 'Parking step omitted: no parking reservation on the trip.',
    });
    return;
  }
  const step = makeStep({
    id: `dep:parking:${deps.legId}`,
    phase: 'departure',
    stepType: STEP_TYPES.PARKING,
    scheduledStart: boundary.start,
    scheduledEnd: boundary.end,
    title: 'Airport parking',
    description:
      'Park at your reserved airport lot and take the shuttle to the terminal.',
    detailSource: {
      lot: nonEmpty(parking.lot) || nonEmpty(parking.parkingLot) || undefined,
      level: nonEmpty(parking.level) || undefined,
      confirmationNumber:
        nonEmpty(parking.confirmationNumber) || nonEmpty(parking.confirmation) || undefined,
      shuttleWait: nonEmpty(parking.shuttleWait) || undefined,
    },
    ctx,
  });
  if (step) steps.push(step);
}

/**
 * Append a flight-anchored "Check-in and security" step (always produced when
 * the boundary resolves). Shared by the departure and return phases.
 */
function appendSecurityStep(boundary, ctx, phase, idPrefix, legId, description, steps) {
  if (!boundary) return;
  const step = makeStep({
    id: `${idPrefix}:security:${legId}`,
    phase,
    stepType: STEP_TYPES.SECURITY,
    scheduledStart: boundary.start,
    scheduledEnd: boundary.end,
    title: 'Check-in and security',
    description,
    ctx,
  });
  if (step) steps.push(step);
}

/**
 * Derive the granular Departure-phase steps (leave home → parking → check-in &
 * security → arrival at gate) for the outbound flight leg.
 *
 * Steps are anchored on the engine boundaries `leaveHomeMs` (departure from
 * origin), `airportEntryMs` (parking), `securityStartMs` (security), and
 * `atGateMs` (gate). The leave-home and parking steps require, respectively, a
 * usable home address and a parking record; when those are absent the step is
 * omitted (Property 6d) and a non-fatal note is recorded. The security and gate
 * steps are flight-anchored and always produced when the boundary times
 * resolve, so a data-poor trip still renders its core departure flow.
 *
 * @param {Object} outboundLeg  The first outbound flight leg.
 * @param {Object} [depCalc]    Departure calculation for this leg.
 * @param {Object} [userProfile] Traveler profile (home address, clearances).
 * @param {Object} [trip]        Full trip (read for an optional parking record).
 * @returns {{ steps: Array<Object>, errors: Array<{ message: string }> }}
 */
export function deriveDepartureSteps(outboundLeg, depCalc, userProfile, trip) {
  const steps = [];
  const errors = [];

  if (!outboundLeg || typeof outboundLeg !== 'object') {
    return { steps, errors };
  }

  const ctx = { userProfile, departureCalc: depCalc };
  const b = resolveBoundaries(outboundLeg, depCalc);

  const inFlight = b[STAGES.IN_FLIGHT];
  const legId = nonEmpty(outboundLeg.flightId) || nonEmpty(outboundLeg.flightNumber) || 'leg0';
  const flightDepartTime = toDate(outboundLeg.departureTime) || (inFlight && inFlight.start);
  const parking = trip && typeof trip === 'object' ? trip.parking : null;
  const deps = { userProfile, depCalc, legId, parking };

  // 1. Leave home — requires a usable home address (otherwise omit).
  appendLeaveHomeStep(b[STAGES.DEPARTURE_FROM_ORIGIN], ctx, deps, steps, errors);

  // 2. Airport parking — requires a parking record on the trip (otherwise omit).
  appendParkingStep(b[STAGES.AIRPORT_ENTRY], ctx, deps, steps, errors);

  // 3. Check-in and security — flight-anchored, always produced.
  appendSecurityStep(
    b[STAGES.SECURITY_CHECKIN],
    ctx,
    'departure',
    'dep',
    legId,
    'Check in for your flight and proceed through the security checkpoint.',
    steps,
  );

  // 4. Arrival at gate — flight-anchored, always produced.
  const gate = b[STAGES.NAVIGATE_TO_GATE];
  if (gate) {
    const gateStep = makeStep({
      id: `dep:gate:${legId}`,
      phase: 'departure',
      stepType: STEP_TYPES.GATE,
      scheduledStart: gate.start,
      scheduledEnd: gate.end,
      title: 'Arrival at gate',
      description:
        'Proceed to your departure gate. Boarding typically begins about 30 minutes before departure.',
      detailSource: flightDetailSource(outboundLeg, flightDepartTime),
      ctx,
    });
    if (gateStep) steps.push(gateStep);
  }

  return { steps, errors };
}

// ── Return phase ────────────────────────────────────────────

/**
 * Append the "Hotel checkout" return step when a hotel with a checkout date is
 * present; otherwise record a non-fatal omission note.
 */
function appendHotelCheckoutStep(hotel, ctx, steps, errors) {
  const checkOut = hotel ? toDate(hotel.checkOutDate) || toDate(hotel.checkOut) : null;
  if (!(hotel && checkOut)) {
    errors.push({
      message: 'Hotel-checkout step omitted: no hotel with a checkout date.',
    });
    return;
  }
  const step = makeStep({
    id: `ret:hotel-checkout:${nonEmpty(hotel.hotelId) || nonEmpty(hotel.id) || 'hotel'}`,
    phase: 'return',
    stepType: STEP_TYPES.HOTEL_CHECKOUT,
    scheduledStart: checkOut,
    scheduledEnd: null,
    title: 'Hotel checkout',
    description:
      'Check out of your hotel. Request an itemized receipt for expense reporting if needed.',
    detailSource: {
      name: nonEmpty(hotel.name) || undefined,
      address: nonEmpty(hotel.address) || undefined,
      confirmationNumber:
        nonEmpty(hotel.confirmationNumber) || nonEmpty(hotel.confirmation) || undefined,
      roomType: nonEmpty(hotel.roomType) || undefined,
      loyaltyStatus: nonEmpty(hotel.loyaltyStatus) || undefined,
    },
    tipSource: hotel,
    ctx,
  });
  if (step) steps.push(step);
}

/**
 * Resolve the return ground-transport source record from the trip, preferring an
 * explicit return transport, then a generic one, then the last car rental.
 */
function resolveGroundSource(trip) {
  if (!(trip && typeof trip === 'object')) return null;
  return (
    trip.returnGroundTransport ||
    trip.groundTransport ||
    (Array.isArray(trip.carRentals) ? trip.carRentals[trip.carRentals.length - 1] : null) ||
    null
  );
}

/**
 * Append the "Ground transportation to airport" return step when the boundary
 * and a transport record are present; otherwise record a non-fatal note.
 */
function appendReturnGroundStep(boundary, groundSource, ctx, depCalc, legId, steps, errors) {
  if (!boundary) return;
  if (!(groundSource && typeof groundSource === 'object')) {
    errors.push({
      message: 'Return ground-transport step omitted: no transport record on the trip.',
    });
    return;
  }
  const travelTime = formatMinutes(resolveTravelMinutes(depCalc));
  const step = makeStep({
    id: `ret:ground-transport:${legId}`,
    phase: 'return',
    stepType: STEP_TYPES.GROUND_TRANSPORT,
    scheduledStart: boundary.start,
    scheduledEnd: boundary.end,
    title: 'Ground transportation to airport',
    description:
      'Travel from your hotel to the airport. Allow buffer time for traffic and check-in.',
    detailSource: {
      pickupLocation:
        nonEmpty(groundSource.pickupLocation) || nonEmpty(groundSource.pickup) || undefined,
      service: nonEmpty(groundSource.service) || nonEmpty(groundSource.company) || undefined,
      travelTime: travelTime || undefined,
      confirmationNumber:
        nonEmpty(groundSource.confirmationNumber)
        || nonEmpty(groundSource.confirmation)
        || undefined,
    },
    tipSource: groundSource,
    ctx,
  });
  if (step) steps.push(step);
}

/**
 * Append the return "Return gate — board flight" step when the boundary resolves.
 */
function appendReturnGateStep(boundary, inboundLeg, flightDepartTime, ctx, legId, steps) {
  if (!boundary) return;
  const step = makeStep({
    id: `ret:gate:${legId}`,
    phase: 'return',
    stepType: STEP_TYPES.GATE,
    scheduledStart: boundary.start,
    scheduledEnd: boundary.end,
    title: 'Return gate — board flight',
    description:
      'Proceed to your departure gate for the return flight and board when your group is called.',
    detailSource: flightDetailSource(inboundLeg, flightDepartTime),
    ctx,
  });
  if (step) steps.push(step);
}

/**
 * Append the "Land and retrieve your vehicle" step when a resolved arrival time
 * is available; otherwise record a non-fatal omission note.
 */
function appendLandingStep(arrival, landedTime, inboundLeg, ctx, legId, steps, errors) {
  if (!(arrival || landedTime)) {
    errors.push({
      message: 'Landing step omitted: no resolved arrival time for the return leg.',
    });
    return;
  }
  const landingStart = (arrival && arrival.start) || landedTime;
  const step = makeStep({
    id: `ret:landing:${legId}`,
    phase: 'return',
    stepType: STEP_TYPES.LANDING,
    scheduledStart: landingStart,
    scheduledEnd: arrival ? arrival.end : null,
    title: 'Land and retrieve your vehicle',
    description:
      'Deplane, collect any checked bags, and head toward ground transport or parking.',
    detailSource: arrivalDetailSource(inboundLeg),
    ctx,
  });
  if (step) steps.push(step);
}

/**
 * Append the "Arrive home — trip complete" step when the boundary and a usable
 * home address are present; otherwise record a non-fatal omission note.
 */
function appendArriveHomeStep(boundary, userProfile, ctx, depCalc, legId, steps, errors) {
  if (!boundary) return;
  if (!hasHomeAddress(userProfile)) {
    errors.push({
      message: 'Arrive-home step omitted: no home address on the traveler profile.',
    });
    return;
  }
  const driveTime = formatMinutes(resolveTravelMinutes(depCalc));
  const step = makeStep({
    id: `ret:arrive-home:${legId}`,
    phase: 'return',
    stepType: STEP_TYPES.ARRIVE_HOME,
    scheduledStart: boundary.start,
    scheduledEnd: boundary.end,
    title: 'Arrive home — trip complete',
    description: driveTime
      ? `Drive home from the airport (about ${driveTime}). Your trip is complete.`
      : 'Drive home from the airport. Your trip is complete.',
    ctx,
  });
  if (step) steps.push(step);
}

/**
 * Derive the granular Return-phase steps (hotel checkout → ground transport to
 * airport → check-in & security → board → land → arrive home) for the inbound
 * flight leg.
 *
 * Flight-anchored steps (security, gate) are always produced when their
 * boundary times resolve. Hotel checkout requires a hotel with a checkout date;
 * ground transport requires a ground-transport / car-rental record; landing
 * requires a resolved arrival time; arrive-home requires a usable home address.
 * Any of these whose inputs are absent are omitted (Property 6d) with a
 * non-fatal note, never fabricated.
 *
 * @param {Object} inboundLeg  The last (homeward) flight leg.
 * @param {Object} [hotel]     Hotel record for checkout.
 * @param {Object} [depCalc]   Departure calculation for the inbound leg.
 * @param {Object} [trip]      Full trip (read for ground transport + home).
 * @returns {{ steps: Array<Object>, errors: Array<{ message: string }> }}
 */
export function deriveReturnSteps(inboundLeg, hotel, depCalc, trip) {
  const steps = [];
  const errors = [];

  if (!inboundLeg || typeof inboundLeg !== 'object') {
    return { steps, errors };
  }

  const userProfile = trip && typeof trip === 'object' ? trip.userProfile : undefined;
  const ctx = { userProfile, departureCalc: depCalc };
  const b = resolveBoundaries(inboundLeg, depCalc);

  const inFlight = b[STAGES.IN_FLIGHT];
  const arrival = b[STAGES.ARRIVAL_NAVIGATION];

  const legId = nonEmpty(inboundLeg.flightId) || nonEmpty(inboundLeg.flightNumber) || 'legR';
  const flightDepartTime = toDate(inboundLeg.departureTime) || (inFlight && inFlight.start);
  const landedTime =
    (inFlight && inFlight.end) || (arrival && arrival.start) || toDate(inboundLeg.arrivalTime);

  // 1. Hotel checkout — requires a hotel with a checkout date.
  appendHotelCheckoutStep(hotel, ctx, steps, errors);

  // 2. Ground transportation to the airport — requires a transport record.
  appendReturnGroundStep(
    b[STAGES.DEPARTURE_FROM_ORIGIN],
    resolveGroundSource(trip),
    ctx,
    depCalc,
    legId,
    steps,
    errors,
  );

  // 3. Check-in and security — flight-anchored, always produced.
  appendSecurityStep(
    b[STAGES.SECURITY_CHECKIN],
    ctx,
    'return',
    'ret',
    legId,
    'Check in for your return flight and proceed through the security checkpoint.',
    steps,
  );

  // 4. Return gate / boarding — flight-anchored, always produced.
  appendReturnGateStep(b[STAGES.NAVIGATE_TO_GATE], inboundLeg, flightDepartTime, ctx, legId, steps);

  // 5. Landing — requires a resolved arrival time.
  appendLandingStep(arrival, landedTime, inboundLeg, ctx, legId, steps, errors);

  // 6. Arrive home — requires a usable home address.
  appendArriveHomeStep(b[STAGES.GROUND_TRANSPORT], userProfile, ctx, depCalc, legId, steps, errors);

  return { steps, errors };
}

// ── Event → step mappers (Destination phase, and shared) ────

/**
 * Map a hotel event onto a check-in (destination) or checkout (return) step.
 *
 * @param {Object} event
 * @param {{ scheduledStart: Date, scheduledEnd: Date|null, source: Object }} common
 * @param {Object} ctx
 * @returns {Object|null}
 */
function hotelEventToStep(event, common, ctx) {
  const { scheduledStart, scheduledEnd, source } = common;
  const isCheckout = typeof event.id === 'string'
    ? event.id.endsWith(':out')
    : /check-?out/i.test(event.title || '');
  const stepType = isCheckout ? STEP_TYPES.HOTEL_CHECKOUT : STEP_TYPES.HOTEL_CHECKIN;
  return makeStep({
    id: nonEmpty(event.id) || `evt:${stepType}`,
    phase: isCheckout ? 'return' : 'destination',
    stepType,
    scheduledStart,
    scheduledEnd,
    title: isCheckout ? 'Hotel checkout' : 'Hotel check-in',
    description: isCheckout
      ? 'Check out of your hotel.'
      : 'Check in at your hotel and settle into your room.',
    detailSource: {
      name: nonEmpty(source.name) || nonEmpty(event.subtitle) || undefined,
      address: nonEmpty(source.address) || undefined,
      confirmationNumber:
        nonEmpty(source.confirmationNumber) || nonEmpty(source.confirmation) || undefined,
      roomType: nonEmpty(source.roomType) || undefined,
      loyaltyStatus: nonEmpty(source.loyaltyStatus) || undefined,
    },
    tipSource: source,
    ctx,
  });
}

/**
 * Map a dining event onto a destination-phase dining step.
 */
function diningEventToStep(event, common, ctx) {
  const { scheduledStart, scheduledEnd, source } = common;
  return makeStep({
    id: nonEmpty(event.id) || 'evt:dining',
    phase: 'destination',
    stepType: STEP_TYPES.DINING,
    scheduledStart,
    scheduledEnd,
    title: nonEmpty(event.subtitle) || 'Dining reservation',
    description: 'Arrive for your dining reservation.',
    detailSource: {
      name: nonEmpty(source.name) || nonEmpty(event.subtitle) || undefined,
      address: nonEmpty(source.address) || undefined,
      confirmationNumber:
        nonEmpty(source.confirmationNumber) || nonEmpty(source.confirmation) || undefined,
      partySize: source.partySize != null ? source.partySize : undefined,
      dressCode: nonEmpty(source.dressCode) || undefined,
    },
    tipSource: source,
    ctx,
  });
}

/**
 * Map a meeting event onto a destination-phase meeting step.
 */
function meetingEventToStep(event, common, ctx) {
  const { scheduledStart, scheduledEnd, source } = common;
  return makeStep({
    id: nonEmpty(event.id) || 'evt:meeting',
    phase: 'destination',
    stepType: STEP_TYPES.MEETING,
    scheduledStart,
    scheduledEnd,
    title: nonEmpty(event.title) || 'Meeting',
    description: nonEmpty(event.subtitle)
      ? `Meeting with ${event.subtitle}.`
      : 'Attend your scheduled meeting.',
    // 'meeting' has no detail-field family; details intentionally empty.
    tipSource: source,
    ctx,
  });
}

/**
 * Map a car-rental event onto a destination-phase ground-transport step.
 */
function carRentalEventToStep(event, common, ctx) {
  const { scheduledStart, scheduledEnd, source } = common;
  return makeStep({
    id: nonEmpty(event.id) || 'evt:ground_transport',
    phase: 'destination',
    stepType: STEP_TYPES.GROUND_TRANSPORT,
    scheduledStart,
    scheduledEnd,
    title: nonEmpty(event.title) || 'Ground transportation',
    description: 'Pick up your ground transportation.',
    detailSource: {
      pickupLocation:
        nonEmpty(source.pickupLocation) || nonEmpty(source.pickup) || undefined,
      service: nonEmpty(source.service) || nonEmpty(source.company) || undefined,
      confirmationNumber:
        nonEmpty(source.confirmationNumber) || nonEmpty(source.confirmation) || undefined,
    },
    tipSource: source,
    ctx,
  });
}

/**
 * Map an in-flight / arrival-navigation stage event onto a flight-arrival step.
 * Departure-side stage events are produced by deriveDepartureSteps, so they map
 * to null here. Returns null when the event is not a mappable arrival stage or
 * its anchor time is unusable.
 */
function stageEventToStep(event, common, ctx) {
  const { scheduledStart, scheduledEnd } = common;
  if (event.stage !== STAGES.IN_FLIGHT && event.stage !== STAGES.ARRIVAL_NAVIGATION) {
    return null;
  }
  const flight = event.stageInputs && event.stageInputs.flight
    ? event.stageInputs.flight
    : {};
  const start = event.stage === STAGES.ARRIVAL_NAVIGATION
    ? scheduledStart
    : (scheduledEnd || scheduledStart); // flight_arrival anchors on landing
  if (!isValidDate(start)) return null;
  return makeStep({
    id: nonEmpty(event.id) || 'evt:flight_arrival',
    phase: 'destination',
    stepType: STEP_TYPES.FLIGHT_ARRIVAL,
    scheduledStart: start,
    scheduledEnd: event.stage === STAGES.ARRIVAL_NAVIGATION ? scheduledEnd : null,
    title: 'Flight arrival',
    description: 'Your flight lands. Remain seated until the seatbelt sign is off, then head to baggage claim.',
    detailSource: arrivalDetailSource(flight),
    ctx,
  });
}

// Dispatch table: event.type → handler. A Map avoids prototype-chain lookups
// for adversarial `type` values (e.g. 'constructor').
const EVENT_STEP_HANDLERS = new Map([
  ['hotel', hotelEventToStep],
  ['dining', diningEventToStep],
  ['meeting', meetingEventToStep],
  ['carRental', carRentalEventToStep],
  ['stage', stageEventToStep],
]);

/**
 * Map a `ClassifiedEvent` (from `buildJourneyEvents`) onto a TripStepData.
 *
 * Handles hotel check-in/out, dining, meeting, car/ground transport, and the
 * flight arrival/landing stage events. Returns null for events that have no
 * granular step representation (or that lack a valid time), so callers can
 * spread + filter the result. Never throws.
 *
 * @param {Object} event  A ClassifiedEvent with `type`, `scheduledStart`, etc.
 * @param {Object} [ctx]  Traveler context: { userProfile, departureCalc, ... }.
 * @returns {Object|null} TripStepData (without `status`) or null.
 */
export function eventToStep(event, ctx = {}) {
  if (!event || typeof event !== 'object') return null;

  const scheduledStart = toDate(event.scheduledStart);
  if (!isValidDate(scheduledStart)) return null;

  const handler = EVENT_STEP_HANDLERS.get(event.type);
  if (!handler) return null;

  const common = {
    scheduledStart,
    scheduledEnd: toDate(event.scheduledEnd),
    source: event.sourceEvent && typeof event.sourceEvent === 'object'
      ? event.sourceEvent
      : {},
  };
  return handler(event, common, ctx);
}

const deriveJourneySteps = {
  STEP_TYPES,
  deriveDepartureSteps,
  deriveReturnSteps,
  eventToStep,
};

export default deriveJourneySteps;
